import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Avatar } from "@/components/Avatar";
import { useChat } from "@/lib/chat";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ExportButton, RangeSelect, num, useRange } from "./shared";

export const EventsLog = () => {
  const [sp, setSp] = useSearchParams();
  const { users, conversations } = useChat();
  const { range, setRange, params } = useRange("7d");
  const [types, setTypes] = useState([]);
  const [data, setData] = useState({ events: [], total: 0 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const f = { user_id: sp.get("user_id") || "", event_type: sp.get("event_type") || "", source: sp.get("source") || "", conversation_id: sp.get("conversation_id") || "" };
  const set = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); setPage(1); };
  const allParams = { ...params, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), q: q || undefined };

  useEffect(() => { api.get("/reports/event-types", { params }).then((r) => {
    const merged = Object.values(r.data.reduce((acc, t) => { acc[t.event_type] = acc[t.event_type] ? { ...acc[t.event_type], count: acc[t.event_type].count + t.count } : { ...t }; return acc; }, {}));
    setTypes(merged.sort((a, b) => b.count - a.count));
  }).catch(() => {}); }, [params]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/reports/events", { params: { ...allParams, page, limit: 50 } }).then((r) => setData(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [page, q, sp, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = Math.max(1, Math.ceil(data.total / 50));
  const convName = (id) => { const c = conversations.find((x) => x.id === id); return c ? (c.type === "channel" ? `#${c.name}` : c.display_name) : id ? id.slice(-6) : ""; };
  return (
    <div data-testid="admin-events">
      <h1>Activity log</h1>
      <p className="lead">Granular, per-user event stream. Filter by person, event type, source or conversation; export any slice as CSV or pull it through the API.</p>
      <div className="tc-admin__toolbar">
        <RangeSelect range={range} setRange={setRange} />
        <select className="tc-select" value={f.user_id} onChange={(e) => set("user_id", e.target.value)} data-testid="filter-user"><option value="">All people</option>{users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}</select>
        <select className="tc-select" value={f.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="filter-event-type"><option value="">All event types</option>{types.map((t) => <option key={t.event_type} value={t.event_type}>{t.event_type} ({t.count})</option>)}</select>
        <select className="tc-select" value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="filter-source"><option value="">All sources</option><option value="client">client</option><option value="server">server</option></select>
        <select className="tc-select" value={f.conversation_id} onChange={(e) => set("conversation_id", e.target.value)} data-testid="filter-conversation"><option value="">All conversations</option>{conversations.map((c) => <option key={c.id} value={c.id}>{c.type === "channel" ? `#${c.name}` : c.display_name}</option>)}</select>
        <input className="tc-input xs" style={{ maxWidth: 220 }} placeholder="Search label / target / path" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="filter-search" />
        <span className="grow" />
        <ExportButton params={allParams} />
      </div>
      <div className="tc-admin-card">
        <div className="tc-admin-card__head">{num(data.total)} events<span className="right">page {page} of {pages}</span></div>
        <div style={{ overflowX: "auto" }}>
          <table className="tc-table" data-testid="events-table">
            <thead><tr><th>Time</th><th>Person</th><th>Event</th><th>Category</th><th>Source</th><th>Target</th><th>Label</th><th>Conversation</th><th>Path</th><th>Metadata</th></tr></thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.id} data-testid={`event-row-${e.id}`}>
                  <td className="mono muted" style={{ whiteSpace: "nowrap" }}>{fmtDateTime(e.ts)}</td>
                  <td>{e.user ? <div className="usercell"><Avatar user={e.user} size={20} radius={4} /><span>{e.user.display_name}</span></div> : <span className="muted">—</span>}</td>
                  <td><span className="mono" style={{ fontWeight: 700 }}>{e.event_type}</span></td>
                  <td className="muted">{e.category}</td>
                  <td><span className={`tc-pill ${e.source}`}>{e.source}</span></td>
                  <td className="mono">{e.target || ""}</td>
                  <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.label || ""}>{e.label || ""}</td>
                  <td className="muted">{convName(e.conversation_id)}</td>
                  <td className="mono muted">{e.path || ""}</td>
                  <td><div className="tc-eventmeta" title={JSON.stringify(e.metadata)}>{JSON.stringify(e.metadata)}</div></td>
                </tr>
              ))}
              {!data.events.length && <tr><td colSpan={10} className="muted" style={{ textAlign: "center", padding: 32 }}>No events match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="tc-pagination">
          <button className="tc-btn tc-btn--outline sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="events-prev">Previous</button>
          <span>{page} / {pages}</span>
          <button className="tc-btn tc-btn--outline sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="events-next">Next</button>
        </div>
      </div>
    </div>
  );
};
